import React, { useCallback, useEffect, useState } from 'react';
import { BookOpen, RefreshCw, Loader2, ArrowRight, ArrowLeft, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { cardLedgerApi } from '../../services/iso8583Api';
import { PageHeader, mtiName, useDialect } from './common';

const STATUS_CHIP = {
  DELIVERED:   'bg-gray-100 text-gray-600',
  WITHHELD:    'bg-amber-100 text-amber-800',
  UNDECODABLE: 'bg-red-100 text-red-700',
};

const FIELD_NAMES = {
  2: 'Card number', 3: 'Processing code', 4: 'Amount', 7: 'Transmission date/time', 11: 'STAN',
  12: 'Local time', 13: 'Local date', 22: 'POS entry mode', 32: 'Acquiring institution', 33: 'Forwarding institution',
  35: 'Track 2', 37: 'Retrieval reference', 38: 'Authorisation code', 39: 'Response code', 41: 'Terminal id',
  42: 'Card acceptor id', 49: 'Currency', 52: 'PIN block', 55: 'Chip data', 70: 'Network management code',
  90: 'Original data elements', 95: 'Replacement amounts', 128: 'MAC',
};

/**
 * Every message on your card link, in both directions, as the certification
 * report's evidence. Stored masked: the card number as its first six and last
 * four digits, and the PIN block, both tracks and chip data withheld.
 */
export default function CardLedger() {
  const { mti } = useDialect();
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ run: 'current', direction: '', mti: '', stan: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { run: filters.run, limit: 200 };
      if (filters.direction) params.direction = filters.direction;
      if (filters.mti.trim()) params.mti = filters.mti.trim();
      if (filters.stan.trim()) params.stan = filters.stan.trim();
      setRows(await cardLedgerApi.list(params));
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-6 max-w-6xl">
      <PageHeader icon={BookOpen} title="Message Ledger">
        Every message on your card link, both ways, newest first — the evidence behind your certification report.
        Card data is masked when it is stored; the full card number is never kept.
      </PageHeader>

      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap items-end gap-3">
        <label className="text-xs text-gray-500">Run
          <select value={filters.run} onChange={set('run')} className="block mt-1 px-2 py-1.5 rounded-lg border border-gray-200 text-sm">
            <option value="current">Current run</option>
            <option value="all">All runs</option>
          </select>
        </label>
        <label className="text-xs text-gray-500">Direction
          <select value={filters.direction} onChange={set('direction')} className="block mt-1 px-2 py-1.5 rounded-lg border border-gray-200 text-sm">
            <option value="">Both</option>
            <option value="TO_SWITCH">You → switch</option>
            <option value="FROM_SWITCH">Switch → you</option>
          </select>
        </label>
        <label className="text-xs text-gray-500">MTI
          <input value={filters.mti} onChange={set('mti')} placeholder={mti('0200')} maxLength={4}
            className="block mt-1 w-20 px-2 py-1.5 rounded-lg border border-gray-200 text-sm font-mono" />
        </label>
        <label className="text-xs text-gray-500">STAN
          <input value={filters.stan} onChange={set('stan')} placeholder="000123" maxLength={6}
            className="block mt-1 w-24 px-2 py-1.5 rounded-lg border border-gray-200 text-sm font-mono" />
        </label>
        <button onClick={load} disabled={loading}
          className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b border-gray-200">
              {['', 'Time (UTC)', 'Direction', 'MTI', 'STAN', 'DE 39', 'Amount', 'Card', 'Status'].map((h) => (
                <th key={h} className="py-2.5 px-3 font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={9} className="py-8 text-center text-gray-400 text-sm">
                No messages yet. Connect and sign on — see Getting Started.
              </td></tr>
            )}
            {rows.map((r) => (
              <React.Fragment key={r.id}>
                <tr className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => setOpen(open === r.id ? null : r.id)}>
                  <td className="py-2 px-3 text-gray-400">{open === r.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                  <td className="py-2 px-3 font-mono text-xs text-gray-600 whitespace-nowrap">{(r.createdAt || '').replace('T', ' ').slice(0, 19)}</td>
                  <td className="py-2 px-3 text-xs whitespace-nowrap">
                    {r.direction === 'TO_SWITCH'
                      ? <span className="inline-flex items-center gap-1 text-primary-700"><ArrowRight className="w-3.5 h-3.5" /> to switch</span>
                      : <span className="inline-flex items-center gap-1 text-emerald-700"><ArrowLeft className="w-3.5 h-3.5" /> from switch</span>}
                  </td>
                  <td className="py-2 px-3"><span className="font-mono text-xs">{r.mti}</span> <span className="text-xs text-gray-400">{mtiName(r.mti)}</span></td>
                  <td className="py-2 px-3 font-mono text-xs">{r.stan || '—'}</td>
                  <td className="py-2 px-3 font-mono text-xs">{r.responseCode || '—'}</td>
                  <td className="py-2 px-3 font-mono text-xs">{r.amount || '—'}</td>
                  <td className="py-2 px-3 font-mono text-xs">{r.panMasked || '—'}</td>
                  <td className="py-2 px-3"><span className={`text-xs px-2 py-0.5 rounded ${STATUS_CHIP[r.status] || STATUS_CHIP.DELIVERED}`}>{r.status}</span></td>
                </tr>
                {open === r.id && (
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <td />
                    <td colSpan={8} className="py-3 px-3">
                      {r.status === 'WITHHELD' && (
                        <p className="text-xs text-amber-800 mb-2">The switch decided this reply and withheld it on purpose, as if lost — a certification scenario. Recover by reversing or repeating.</p>
                      )}
                      <table className="text-xs">
                        <tbody>
                          {Object.entries(r.fields || {}).map(([de, v]) => (
                            <tr key={de}>
                              <td className="pr-3 py-0.5 font-mono text-gray-500 whitespace-nowrap">DE {de}</td>
                              <td className="pr-3 py-0.5 text-gray-500 whitespace-nowrap">{FIELD_NAMES[de] || ''}</td>
                              <td className="py-0.5 font-mono text-gray-900 break-all">{v}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {r.findings && (
                        <pre className="mt-3 text-xs text-red-800 bg-red-50 border border-red-100 rounded p-2 whitespace-pre-wrap">{r.findings}</pre>
                      )}
                      <p className="text-[11px] text-gray-400 mt-2">link {r.linkId} · run {r.runRef}</p>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
